import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Car,
  CheckSquare,
  ChevronRight,
  ChevronsUpDown,
  CircleDollarSign,
  Database,
  DollarSign,
  Fingerprint,
  FlaskConical,
  Globe,
  History,
  Layers,
  SlidersHorizontal,
  LogOut,
  LucideIcon,
  Scale,
  Settings,
  Shield,
  UserSearch,
  Users,
  Wallet,
  LayoutGrid,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { JurisdictionFlag } from "@/components/atoms";
import { useAuth } from "@/hooks/useAuth";
import { useDemoStatus } from "@/hooks/useDemoStatus";
import useModuleCatalog from "@/hooks/useModuleCatalog";
import apiService, { type CatalogModule, type MySpendingLimitResponse } from "@/services/api";
import { useAppStore } from "@/store/appStore";
import { BALANCE_CHANGED_EVENT } from "@/utils/billingEvents";
import { compareJurisdictions, jurisdictionOf, jurisdictionTitle, stripJurisdictionSuffix } from "@/utils/jurisdiction";
import { pageForModule } from "@/utils/modulePages";
import { PERSON_REPORT_BASE, PERSON_REPORT_PAGE, useActivePageKey } from "@/utils/pageRoutes";
import { cn } from "@/utils";
import { DEMO_HIDDEN_PAGES, checksWord, demoUntil } from "@/utils/demo";

interface NavItem {
  id: string;
  title: string;
  icon: LucideIcon;
  page: string;
  adminOnly?: boolean;
  /**
   * Сбросить currentReportJobId перед сменой страницы. Нужен для пунктов,
   * которые означают «начать заново» (Проверка физлица): иначе в сторе
   * остаётся ссылка на прошлый отчёт и кнопка «Создать» ведёт себя как
   * «вернуться к отчёту».
   */
  resetReportJob?: boolean;
}

interface MenuSection {
  id: string;
  title: string;
  icon: LucideIcon;
  items: { id: string; title: string; page: string }[];
}

// Пункта «Дашборд» здесь больше нет: он вёл на ту же страницу complex_check,
// что и «Проверка физлица», из-за чего в меню подсвечивались сразу два пункта.
const primaryItems: NavItem[] = [
  {
    id: "person_check",
    title: "Проверка физлица",
    icon: UserSearch,
    page: "complex_check",
    resetReportJob: true,
  },
  { id: "company_check", title: "Проверка организации", icon: Building2, page: "company_check" },
  { id: "vehicle_check", title: "Проверка транспорта", icon: Car, page: "vehicle_check" },
  { id: "crypto_check", title: "Проверка криптоадреса", icon: Wallet, page: "crypto_check" },
  { id: "history", title: "История", icon: History, page: "history" },
  { id: "monitoring", title: "Мониторинг субъектов", icon: Activity, page: "monitoring" },
  // Каталог показывает весь реестр, а не только российские проверки из
  // разделов ниже: международные модули иначе остаются невидимыми.
  { id: "modules_catalog", title: "Каталог проверок", icon: LayoutGrid, page: "modules_catalog" },
];

const adminItems: NavItem[] = [
  { id: "health_dashboard", title: "Состояние системы", icon: Activity, page: "health_dashboard", adminOnly: true },
  { id: "fetcher_dashboard", title: "Фетчеры", icon: Database, page: "fetcher_dashboard", adminOnly: true },
  { id: "clients_list", title: "Клиенты", icon: Users, page: "clients_list", adminOnly: true },
  { id: "demo_access", title: "Демо-доступы", icon: FlaskConical, page: "demo_access", adminOnly: true },
  { id: "billing_settings", title: "Тарифы и пакеты", icon: DollarSign, page: "billing_settings", adminOnly: true },
  { id: "admin_invoices", title: "Счета клиентов", icon: DollarSign, page: "admin_invoices", adminOnly: true },
  {
    id: "admin_subscriptions",
    title: "Очередь пакетов",
    icon: Layers,
    page: "admin_subscriptions",
    adminOnly: true,
  },
  {
    id: "platform_risk_matrices",
    title: "Матрицы рисков",
    icon: SlidersHorizontal,
    page: "platform_risk_matrices",
    adminOnly: true,
  },
];

const menuSections: MenuSection[] = [
  {
    id: "validation",
    title: "Валидация данных",
    icon: CheckSquare,
    items: [
      { id: "address_validation", title: "Адрес", page: "address_validation" },
      { id: "phone_validation", title: "Телефон", page: "phone_validation" },
      { id: "email_validation", title: "E-mail", page: "email_validation" },
      { id: "name_validation", title: "ФИО", page: "name_validation" },
      { id: "document_validation", title: "Паспорт", page: "document_validation" },
      { id: "tax_id_validation", title: "ИНН", page: "tax_id_validation" },
      { id: "snils_validation", title: "СНИЛС", page: "snils_validation" },
      { id: "ip_address_validation", title: "IP-адрес", page: "ip_address_validation" },
    ],
  },
  {
    id: "verification",
    title: "Верификация",
    icon: BadgeCheck,
    items: [
      { id: "fns_tax_id_verification", title: "ИНН по паспорту", page: "fns_tax_id_verification" },
      { id: "passport_verification", title: "Паспорт РФ", page: "passport_verification" },
      { id: "mvd_driver_license_verification", title: "Вод. удостоверение", page: "mvd_driver_license_verification" },
      { id: "sfr_ssn_verification", title: "СНИЛС", page: "sfr_ssn_verification" },
      { id: "obrnadzor_education_verification", title: "Образование", page: "obrnadzor_education_verification" },
    ],
  },
  {
    id: "administrative",
    title: "Адм. нарушения",
    icon: AlertTriangle,
    items: [
      { id: "admin_disqualified", title: "Дисквалификация", page: "admin_disqualified" },
      { id: "au_violations", title: "Нарушения АУ", page: "au_violations" },
    ],
  },
  {
    id: "criminal",
    title: "Криминал и розыск",
    icon: Shield,
    items: [
      { id: "mvd_wanted", title: "Федеральный розыск", page: "mvd_wanted" },
      { id: "criminal_wanted", title: "Розыск преступников", page: "criminal_wanted" },
      { id: "debtor_wanted", title: "Розыск должников", page: "debtor_wanted" },
      { id: "fsin_criminal_wanted", title: "Побег из МЛС", page: "fsin_criminal_wanted" },
      { id: "interpol_red_notice", title: "Красные карточки", page: "interpol_red_notice" },
      { id: "interpol_yellow_notice", title: "Жёлтые карточки", page: "interpol_yellow_notice" },
      { id: "interpol_un_sanctions", title: "Санкции ООН", page: "interpol_un_sanctions" },
    ],
  },
  {
    id: "financial",
    title: "Долги и залоги",
    icon: CircleDollarSign,
    items: [
      { id: "execution_proceedings", title: "Исп. производства", page: "execution_proceedings" },
      { id: "alimony_debt", title: "Алименты", page: "alimony_debt" },
      { id: "tax_debt", title: "Налоговая задолж.", page: "tax_debt" },
      { id: "account_suspension", title: "Блокировка счетов", page: "account_suspension" },
      { id: "credit_scoring", title: "Кредитный скоринг", page: "credit_scoring" },
      { id: "credit_rating", title: "Кредитный рейтинг", page: "credit_rating" },
      { id: "pledge", title: "Залоги", page: "pledge" },
    ],
  },
  {
    id: "courts_bankruptcy",
    title: "Суды и банкротство",
    icon: Scale,
    items: [
      { id: "general_courts", title: "Суды общей юрисд.", page: "general_courts" },
      { id: "arbitration_courts", title: "Арбитражные суды", page: "arbitration_courts" },
      { id: "bankruptcy", title: "Банкротство", page: "bankruptcy" },
      { id: "fns_subsidiary_defendant", title: "Субсидиарка", page: "fns_subsidiary_defendant" },
    ],
  },
  {
    id: "negative",
    title: "Негативные реестры",
    icon: AlertOctagon,
    items: [
      { id: "sanctions_screening", title: "Санкционный скрининг", page: "sanctions_screening" },
      { id: "pep_screening", title: "Глобальный PEP", page: "pep_screening" },
      { id: "terrorist_extreme", title: "Терроризм", page: "terrorist_extreme" },
      { id: "foreign_agent", title: "Иноагенты", page: "foreign_agent" },
      { id: "corruption", title: "Коррупция", page: "corruption" },
      { id: "cbr_warning_list", title: "Нелегальные ФО", page: "cbr_warning_list" },
      { id: "fns_mass_leaders", title: "Масс. руководители", page: "fns_mass_leaders" },
      { id: "fns_mass_founders", title: "Масс. учредители", page: "fns_mass_founders" },
      // Ниже — модули без своей страницы: открываются формой по паспорту из
      // реестра. Раньше их в меню просто не было.
      { id: "rupep_pep", title: "RuPEP — ПДЛ", page: "compliance.rus.rupep_pep" },
      { id: "fns_azart_list", title: "Перечень ФНС: азартные игры", page: "compliance.rus.fns_azart_list" },
      { id: "fns_lottery_list", title: "Перечень ФНС: лотереи", page: "compliance.rus.fns_lottery_list" },
      { id: "cbr_warning_list_ul", title: "Нелегальные ФО (ЮЛ)", page: "compliance.rus.cbr_warning_list" },
    ],
  },
  {
    id: "digital_footprint",
    title: "Цифровой след",
    icon: Fingerprint,
    items: [
      { id: "osint_phone", title: "Телефон (OSINT)", page: "osint_phone" },
      { id: "osint_email", title: "E-mail (OSINT)", page: "osint_email" },
      { id: "osint_username", title: "Ник (OSINT)", page: "osint_username" },
      { id: "osint_domain", title: "Домен (OSINT)", page: "osint_domain" },
      { id: "osint_ip", title: "IP-адрес (OSINT)", page: "osint_ip" },
      { id: "osint_crypto", title: "Криптокошелёк (OSINT)", page: "osint_crypto" },
    ],
  },
  {
    id: "migration",
    title: "Миграция",
    icon: Globe,
    items: [{ id: "mvd_controlled_persons", title: "Контролируемые лица", page: "mvd_controlled_persons" }],
  },
  {
    id: "business",
    title: "Организации",
    icon: BriefcaseBusiness,
    items: [
      { id: "company_report", title: "Отчёт по компании", page: "company_report" },
      { id: "financial_score", title: "Финансовое состояние", page: "business.rus.financial_score" },
      { id: "legal_entity_enrichment", title: "Данные ЮЛ/ИП", page: "business.rus.legal_entity_enrichment" },
      { id: "individual_entrepreneur", title: "Статус ИП", page: "business.rus.individual_entrepreneur" },
      { id: "self_employment", title: "Самозанятость", page: "business.rus.self_employment" },
      { id: "person_connections", title: "Связи физлица с компаниями", page: "business.rus.person_connections" },
      { id: "person_business_check", title: "Бизнес-проверка физлица", page: "business.rus.person_business_check" },
    ],
  },
];

/** Выбранная юрисдикция переживает перезагрузку — но только в этом браузере. */
const JURISDICTION_STORAGE_KEY = "sidebar.jurisdiction";

function readStoredJurisdiction(): string {
  try {
    return localStorage.getItem(JURISDICTION_STORAGE_KEY) || "rus";
  } catch {
    return "rus";
  }
}

/** Порядок и оформление разделов, собираемых по реестру. */
const CATEGORY_SECTIONS: ReadonlyArray<{ id: string; title: string; icon: LucideIcon }> = [
  { id: "verification", title: "Верификация", icon: BadgeCheck },
  { id: "compliance", title: "Комплаенс", icon: Shield },
  { id: "business", title: "Организации", icon: BriefcaseBusiness },
  { id: "validation", title: "Валидация данных", icon: CheckSquare },
  { id: "osint", title: "Цифровой след", icon: Fingerprint },
  { id: "report", title: "Сводные отчёты", icon: Layers },
];

/**
 * Разделы меню для одной юрисдикции, собранные по живому реестру.
 *
 * Для России дерево остаётся рукописным: там полсотни модулей, и порядок
 * с короткими названиями важнее автоматизма. Для остальных юрисдикций
 * рукописный список означал бы, что новая страна не видна в кабинете, пока
 * кто-нибудь не вспомнит дописать меню — так и вышло с Казахстаном,
 * Беларусью, Кыргызстаном, Узбекистаном, Арменией, Польшей и ЕС.
 */
function sectionsForJurisdiction(modules: CatalogModule[], code: string): MenuSection[] {
  const mine = modules.filter((m) => jurisdictionOf(m.key) === code);

  return CATEGORY_SECTIONS.map(({ id, title, icon }) => ({
    id,
    title,
    icon,
    items: mine
      .filter((m) => m.category === id)
      .map((m) => ({
        id: m.key,
        title: stripJurisdictionSuffix(m.name),
        page: pageForModule(m.key),
      }))
      .sort((a, b) => a.title.localeCompare(b.title, "ru")),
  })).filter((section) => section.items.length > 0);
}

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** Когда true — sidebar в icon-rail режиме (узкий, только иконки). На mobile drawer всё равно показывается полным. */
  collapsed?: boolean;
  /** Hover на toggle-handle: даёт «дыхание» правой границы (расширение rail / сужение full). */
  breathing?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen = false,
  onMobileClose,
  collapsed = false,
  breathing = false,
}) => {
  // Активный пункт считаем по адресу, а не по стору: при открытии прямой
  // ссылки подсветка обязана быть верной сразу, до синхронизации стора.
  const activePage = useActivePageKey();
  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentPage, setCurrentReportJobId } = useAppStore();
  const { user, logout } = useAuth();
  const { modules } = useModuleCatalog();

  const isAdmin = user?.role === "ADMIN" || user?.role === "MANAGER" || user?.role === "ACCOUNTANT";
  const isClient = user?.role === "CLIENT";
  const demo = useDemoStatus();

  const filteredAdminItems = adminItems.filter((item) => !item.adminOnly || isAdmin);

  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [jurisdiction, setJurisdiction] = useState<string>(readStoredJurisdiction);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [daysTotal, setDaysTotal] = useState<number | null>(null);
  const [myLimit, setMyLimit] = useState<MySpendingLimitResponse | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isClient) return;

    let cancelled = false;
    const refresh = (): void => {
      Promise.all([
        apiService.getBillingBalance().catch(() => null),
        apiService.getMySpendingLimit().catch(() => null),
      ]).then(([balanceRes, limitRes]) => {
        if (cancelled) return;
        if (balanceRes) {
          setBalance(balanceRes.total_available);
          setDaysRemaining(balanceRes.days_remaining);
          setDaysTotal(balanceRes.days_total);
        }
        setMyLimit(limitRes);
      });
    };

    refresh();
    // Любой запуск платного модуля шлёт billing:balance-changed —
    // подтягиваем актуальный остаток без ручного reload страницы.
    window.addEventListener(BALANCE_CHANGED_EVENT, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(BALANCE_CHANGED_EVENT, refresh);
    };
  }, [isClient]);

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  const toggleSection = (sectionId: string): void => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId],
    );
  };

  // Юрисдикции берём из реестра, а не из списка в коде: появилась страна —
  // появился пункт, без правки фронтенда.
  const jurisdictions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of modules) {
      const code = jurisdictionOf(m.key);
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => compareJurisdictions(a[0], b[0]));
  }, [modules]);

  const sections = useMemo(
    () => (jurisdiction === "rus" ? menuSections : sectionsForJurisdiction(modules, jurisdiction)),
    [modules, jurisdiction],
  );

  const changeJurisdiction = (code: string): void => {
    setJurisdiction(code);
    try {
      localStorage.setItem(JURISDICTION_STORAGE_KEY, code);
    } catch {
      // Приватное окно или запрет на запись: выбор просто не переживёт вкладку.
    }
  };

  // Переход по прямой ссылке на модуль другой страны переключает меню на неё:
  // иначе открытая страница не подсвечена ни одним пунктом.
  useEffect(() => {
    if (!activePage.includes(".")) return;
    const code = jurisdictionOf(activePage);
    setJurisdiction((prev) => (prev === code ? prev : code));
  }, [activePage]);

  // Смена юрисдикции раскрывает её разделы: у России дерево длинное и
  // открывается по частям, у остальных стран модулей единицы — прятать их за
  // ещё одним кликом незачем. Ручное сворачивание при этом не отменяется:
  // раскладку пересобираем только при самой смене страны.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (seededFor.current === jurisdiction) return;
    if (jurisdiction !== "rus" && sections.length === 0) return; // реестр ещё не приехал
    seededFor.current = jurisdiction;
    setExpandedSections(jurisdiction === "rus" ? [] : sections.map((s) => s.id));
  }, [jurisdiction, sections]);

  const handlePageChange = (page: string, opts?: { resetReportJob?: boolean }): void => {
    if (opts?.resetReportJob) {
      setCurrentReportJobId(null);
    }
    // dev-флаг ?synthetic=1 чистить вручную больше не нужно: setCurrentPage
    // уходит в navigate по чистому адресу, query-строка отбрасывается сама.
    setCurrentPage(page);
    if (onMobileClose) onMobileClose();
  };

  const subtitle = ((): { primary: string; hint?: string } => {
    // Демо: денег нет, проверки считает квота — рубли показывали бы «0 ₽».
    if (demo?.is_demo) {
      if (demo.sandbox_key) return { primary: "Демо · песочница", hint: "проверки на вымышленных данных" };
      const left = demo.remaining;
      return {
        primary: left == null ? "Демо · без лимита проверок" : `Демо · осталось ${left} ${checksWord(left)}`,
        hint: demo.expires_at ? `до ${demoUntil(demo.expires_at)}, данные вымышлены` : "данные вымышлены",
      };
    }
    if (isClient && balance !== null) {
      const fmt = (v: number): string =>
        new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(Math.max(0, v));
      const hasLimit = myLimit?.has_limits === true;

      // Безлимит: видит весь баланс компании.
      if (!hasLimit) {
        return {
          primary: `Клиент · до ${fmt(balance)} ₽`,
          hint: "общий баланс компании, без личного лимита",
        };
      }

      const preset = myLimit?.preset;
      const capRemaining = myLimit?.cap_remaining ?? 0;
      const reserve = myLimit?.reserve_per_package ?? 0;
      const spent = myLimit?.spent_in_package ?? 0;

      // Только гарантия: cap = reserve, личный «карман».
      if (preset === "reserved") {
        const available = Math.min(capRemaining, balance);
        return {
          primary: `Клиент · ${fmt(available)} ₽`,
          hint: "зарезервировано лично за вами",
        };
      }

      // Только потолок: из общего баланса, может уменьшиться.
      if (preset === "cap_only") {
        const available = Math.min(capRemaining, balance);
        return {
          primary: `Клиент · до ${fmt(available)} ₽`,
          hint: "из общего баланса компании, может уменьшиться",
        };
      }

      // Гарантия + потолок: показываем обе части.
      if (preset === "reserved_cap") {
        const reserveLeft = Math.max(0, reserve - spent);
        const extraLeft = Math.max(0, capRemaining - reserveLeft);
        const total = Math.min(reserveLeft + extraLeft, balance);
        return {
          primary: `Клиент · до ${fmt(total)} ₽`,
          hint: `${fmt(reserveLeft)} ₽ резерв + до ${fmt(extraLeft)} ₽ из общего`,
        };
      }

      return { primary: `Клиент · ${fmt(balance)} ₽` };
    }
    if (user?.role === "ADMIN") return { primary: "Администратор" };
    if (user?.role === "MANAGER") return { primary: "Менеджер" };
    if (user?.role === "ACCOUNTANT") return { primary: "Бухгалтер" };
    if (isClient) return { primary: "Клиент" };
    return { primary: "Пользователь" };
  })();

  const titleText = user?.email || "Контекст";
  const isRail = collapsed && !mobileOpen;
  const initials = (user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={onMobileClose} />}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed z-40 flex flex-col",
          "bg-surface-sidebar border-r border-line",
          "rounded-tl-panel",
          // Desktop — icon-rail when collapsed, full when expanded.
          "hidden lg:flex left-0 top-[52px] bottom-10",
          // Mobile drawer
          mobileOpen && "!flex !left-0 !top-0 !bottom-0 !rounded-none",
        )}
        style={{
          width: mobileOpen ? 256 : collapsed ? (breathing ? 80 : 64) : breathing ? 244 : 256,
          transition: "width 220ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Client / user card */}
        <div ref={userMenuRef} className={cn("relative", isRail && "flex flex-col items-center")}>
          {isRail ? (
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              title={titleText}
              className={cn(
                "grid place-items-center w-9 h-9 mt-3 mb-1 rounded-md shrink-0",
                "bg-surface-hover text-ink-1 font-medium text-fs-xs",
                "hover:bg-pressed transition-colors",
              )}
            >
              {initials}
            </button>
          ) : (
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              className={cn(
                "flex items-center gap-2 mt-3 mb-1 mx-2 w-[calc(100%-1rem)] px-2 py-1.5",
                "text-left rounded-lg transition-colors",
                "hover:bg-surface-hover",
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-fs-sm tracking-[-0.005em] text-ink-1 truncate leading-tight">
                  {titleText}
                </div>
                <div className="mt-0.5 text-fs-2xs text-ink-3 truncate leading-tight">{subtitle.primary}</div>
                {subtitle.hint && (
                  <div className="text-fs-2xs text-ink-4 truncate leading-tight" title={subtitle.hint}>
                    {subtitle.hint}
                  </div>
                )}
              </div>
              <ChevronsUpDown size={14} strokeWidth={1.75} className="shrink-0 text-ink-4" />
            </button>
          )}

          {!isRail && isClient && daysTotal !== null && daysTotal > 0 && daysRemaining !== null && (
            <div
              className="mx-3 mb-2 h-[3px] rounded-pill bg-surface-hover relative overflow-hidden"
              title={`Осталось ${daysRemaining} из ${daysTotal} дней пакета`}
            >
              <div
                className="absolute inset-y-0 left-0 bg-accent rounded-pill"
                style={{ width: `${Math.min(100, Math.max(2, (daysRemaining / daysTotal) * 100))}%` }}
              />
            </div>
          )}

          {userMenuOpen && (
            <div
              className={cn(
                "absolute top-full mt-1 z-50",
                isRail ? "left-1/2 -translate-x-1/2 w-44" : "left-2 right-2",
                "bg-surface-elevated border border-line rounded-lg shadow-shell-md py-1",
              )}
            >
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  handlePageChange("settings");
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-fs-sm text-ink-2 hover:bg-surface-hover hover:text-ink-1 transition-colors"
              >
                <Settings size={14} className="text-ink-3" />
                <span>Настройки</span>
              </button>
              <div className="my-1 border-t border-line" />
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-fs-sm text-ink-2 hover:bg-surface-hover hover:text-ink-1 transition-colors"
              >
                <LogOut size={14} className="text-ink-3" />
                <span>Выйти</span>
              </button>
            </div>
          )}
        </div>

        <nav
          className={cn(
            "flex-1 overflow-y-auto",
            isRail ? "overflow-x-visible" : "overflow-x-hidden",
            isRail ? "px-0 pt-2 pb-3" : "px-2 pt-2 pb-3",
            // В rail-режиме scrollbar полностью скрыт (иначе крадёт 10px и смещает иконки).
            // В full-режиме — auto-hide pill scrollbar с зарезервированным местом.
            isRail
              ? "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              : cn(
                  "[scrollbar-gutter:stable]",
                  "[&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent",
                  "[&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:border-[3px] [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:rounded-full",
                  "hover:[&::-webkit-scrollbar-thumb]:bg-line-strong [&::-webkit-scrollbar-thumb]:bg-clip-padding",
                ),
          )}
        >
          {/* Primary */}
          <div className={isRail ? "flex flex-col items-center" : ""}>
            {primaryItems
              .filter((item) => !(demo?.is_demo && DEMO_HIDDEN_PAGES.has(item.page)))
              .map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  active={activePage === item.page}
                  collapsed={isRail}
                  onClick={() => handlePageChange(item.page, { resetReportJob: item.resetReportJob })}
                />
              ))}
          </div>

          {/* Modules */}
          <div className={cn(isRail ? "mt-3 flex flex-col items-center" : "mt-3.5")}>
            {!isRail && <SectionLabel>Модули</SectionLabel>}
            {jurisdictions.length > 1 && (
              <JurisdictionSwitch
                value={jurisdiction}
                options={jurisdictions}
                collapsed={isRail}
                onChange={changeJurisdiction}
              />
            )}
            {sections.map((section) => (
              <DisclosureSection
                key={section.id}
                section={section}
                expanded={expandedSections.includes(section.id)}
                activePage={activePage}
                collapsed={isRail}
                onToggle={() => toggleSection(section.id)}
                onItemClick={handlePageChange}
              />
            ))}
          </div>

          {/* Admin */}
          {filteredAdminItems.length > 0 && (
            <div className={cn(isRail ? "mt-3 flex flex-col items-center" : "mt-3.5")}>
              {!isRail && <SectionLabel>Администрирование</SectionLabel>}
              {filteredAdminItems.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  active={activePage === item.page}
                  collapsed={isRail}
                  onClick={() => handlePageChange(item.page, { resetReportJob: item.resetReportJob })}
                />
              ))}
            </div>
          )}

          {/* Dev-only: синтетический отчёт для отработки дизайна */}
          {import.meta.env.DEV && (
            <div className={cn(isRail ? "mt-3 flex flex-col items-center" : "mt-3.5")}>
              {!isRail && <SectionLabel>Dev</SectionLabel>}
              <NavLink
                item={{
                  id: "synthetic_report",
                  title: "Синтетический отчёт",
                  icon: FlaskConical,
                  page: "complex_report",
                }}
                active={activePage === PERSON_REPORT_PAGE && location.search.includes("synthetic=1")}
                collapsed={isRail}
                onClick={() => {
                  // Синтетика рендерится без job_id, поэтому идём на «голый»
                  // /reports с флагом и сбрасываем прошлый отчёт — иначе
                  // страница параллельно подключила бы SSE к реальному job'у.
                  setCurrentReportJobId(null);
                  navigate(`${PERSON_REPORT_BASE}?synthetic=1`);
                  if (onMobileClose) onMobileClose();
                }}
              />
            </div>
          )}
        </nav>
      </aside>
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Выбор юрисдикции над деревом модулей.
 *
 * Ключи модулей давно трёхсегментные (`категория.юрисдикция.имя`), а меню
 * оставалось одностраничным списком российских проверок: страны, добавленные
 * на бэкенде, в кабинете не существовали. Переключатель — одно измерение
 * поверх прежних категорий, а не второе дерево рядом с ними.
 */
const JurisdictionSwitch: React.FC<{
  value: string;
  options: ReadonlyArray<[string, number]>;
  collapsed?: boolean;
  onChange: (code: string) => void;
}> = ({ value, options, collapsed, onChange }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ left: collapsed ? r.right + 8 : r.left, top: collapsed ? r.top : r.bottom + 4 });
    }
  }, [open, collapsed]);

  useEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={ref}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Юрисдикция: ${jurisdictionTitle(value)}`}
        aria-expanded={open}
        className={cn(
          "transition-colors shrink-0",
          collapsed
            ? "grid place-items-center my-0.5 w-9 h-9 rounded-lg hover:bg-surface-hover text-fs-2xs font-medium text-ink-2"
            : cn(
                "w-full flex items-center gap-2.5 px-2.5 py-1.5 my-px rounded-lg",
                "text-fs-sm text-left text-ink-2 hover:bg-surface-hover hover:text-ink-1",
              ),
        )}
      >
        {collapsed ? (
          <JurisdictionFlag code={value} className="h-3.5 w-5" />
        ) : (
          <>
            <JurisdictionFlag code={value} className="h-3.5 w-5" />
            <span className="flex-1 truncate">{jurisdictionTitle(value)}</span>
            <ChevronsUpDown size={13} strokeWidth={1.75} className="shrink-0 text-ink-4" />
          </>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[99]" onClick={() => setOpen(false)} />
            <div
              className={cn(
                "fixed z-[100] min-w-[200px] py-1.5",
                "bg-surface-elevated border border-line rounded-lg shadow-shell-md",
              )}
              style={{ left: pos.left, top: pos.top }}
            >
              {options.map(([code, count]) => (
                <button
                  key={code}
                  onClick={() => {
                    onChange(code);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-fs-sm transition-colors",
                    code === value
                      ? "bg-surface-hover text-ink-1 font-medium"
                      : "text-ink-2 hover:bg-surface-hover hover:text-ink-1",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <JurisdictionFlag code={code} className="h-3.5 w-5" />
                    {jurisdictionTitle(code)}
                  </span>
                  <span className="text-fs-2xs text-ink-4">{count}</span>
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-2.5 pt-2 pb-1.5 font-mono text-fs-2xs uppercase tracking-[0.12em] text-ink-3">{children}</div>
);

const NavLink: React.FC<{ item: NavItem; active: boolean; collapsed?: boolean; onClick: () => void }> = ({
  item,
  active,
  collapsed,
  onClick,
}) => {
  const Icon = item.icon;

  if (collapsed) {
    return <RailNavLinkButton item={item} active={active} onClick={onClick} />;
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 px-2.5 py-1.5 my-px rounded-lg",
        "text-fs-sm text-left transition-colors",
        active ? "bg-surface-hover text-ink-1 font-medium" : "text-ink-2 hover:bg-surface-hover hover:text-ink-1",
      )}
    >
      <Icon size={16} strokeWidth={1.75} className={cn("shrink-0", active ? "text-accent" : "text-ink-3")} />
      <span className="truncate">{item.title}</span>
    </button>
  );
};

const DisclosureSection: React.FC<{
  section: MenuSection;
  expanded: boolean;
  activePage: string;
  collapsed?: boolean;
  onToggle: () => void;
  onItemClick: (page: string) => void;
}> = ({ section, expanded, activePage, collapsed, onToggle, onItemClick }) => {
  const Icon = section.icon;

  if (collapsed) {
    return <RailDisclosurePopover section={section} activePage={activePage} onItemClick={onItemClick} />;
  }

  return (
    <>
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          "w-full flex items-center gap-2.5 px-2.5 py-1.5 my-px rounded-lg",
          "text-fs-sm text-left text-ink-2 hover:bg-surface-hover hover:text-ink-1 transition-colors",
        )}
      >
        <Icon size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" />
        <span className="flex-1 truncate">{section.title}</span>
        <ChevronRight
          size={13}
          strokeWidth={1.75}
          className={cn("shrink-0 text-ink-4 transition-transform duration-200", expanded && "rotate-90")}
        />
      </button>

      {expanded && (
        <div className="pl-[25px] pb-1">
          {section.items.map((item) => {
            const active = activePage === item.page;
            return (
              <button
                key={item.id}
                onClick={() => onItemClick(item.page)}
                className={cn(
                  "block w-full text-left px-2.5 py-1 rounded-md text-fs-sm transition-colors",
                  active
                    ? "bg-surface-hover text-ink-1 font-medium"
                    : "text-ink-2 hover:bg-surface-hover hover:text-ink-1",
                )}
              >
                {item.title}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

// ─── Icon-rail floating tooltip / popover (через React Portal в body, чтобы
// не клипиться overflow:auto на nav) ─────────────────────────────────────────

const TOOLTIP_GAP = 12;

const RailNavLinkButton: React.FC<{ item: NavItem; active: boolean; onClick: () => void }> = ({
  item,
  active,
  onClick,
}) => {
  const Icon = item.icon;
  const ref = useRef<HTMLButtonElement>(null);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (hover && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ left: r.right + TOOLTIP_GAP, top: r.top + r.height / 2 });
    }
  }, [hover]);

  return (
    <>
      <button
        ref={ref}
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={item.title}
        className={cn(
          "grid place-items-center my-0.5 w-9 h-9 rounded-lg transition-colors shrink-0",
          active ? "bg-accent-soft" : "hover:bg-surface-hover",
        )}
      >
        <Icon size={16} strokeWidth={1.75} className={active ? "text-accent" : "text-ink-2"} />
      </button>

      {hover &&
        pos &&
        createPortal(
          <span
            className={cn(
              "fixed z-[100] -translate-y-1/2 whitespace-nowrap pointer-events-none",
              "px-2.5 py-1 rounded-md bg-zinc-900 text-white text-fs-xs font-medium shadow-shell-md",
            )}
            style={{ left: pos.left, top: pos.top }}
          >
            {item.title}
          </span>,
          document.body,
        )}
    </>
  );
};

const RailDisclosurePopover: React.FC<{
  section: MenuSection;
  activePage: string;
  onItemClick: (page: string) => void;
}> = ({ section, activePage, onItemClick }) => {
  const Icon = section.icon;
  const ref = useRef<HTMLButtonElement>(null);
  const [hover, setHover] = useState(false);
  const [popoverHover, setPopoverHover] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const open = hover || popoverHover;

  useEffect(() => {
    if (open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ left: r.right + 8, top: r.top });
    }
  }, [open]);

  return (
    <>
      <button
        ref={ref}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={section.title}
        className="grid place-items-center my-0.5 w-9 h-9 rounded-lg hover:bg-surface-hover transition-colors shrink-0"
      >
        <Icon size={16} strokeWidth={1.75} className="text-ink-2" />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            onMouseEnter={() => setPopoverHover(true)}
            onMouseLeave={() => setPopoverHover(false)}
            className={cn(
              "fixed z-[100] min-w-[220px] py-1.5",
              "bg-surface-elevated border border-line rounded-lg shadow-shell-md",
            )}
            style={{ left: pos.left, top: pos.top }}
          >
            <div className="px-3 pt-1 pb-1.5 font-mono text-fs-2xs uppercase tracking-[0.12em] text-ink-3">
              {section.title}
            </div>
            {section.items.map((item) => {
              const active = activePage === item.page;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onItemClick(item.page);
                    setHover(false);
                    setPopoverHover(false);
                  }}
                  className={cn(
                    "block w-full text-left px-3 py-1.5 text-fs-sm transition-colors",
                    active
                      ? "bg-surface-hover text-ink-1 font-medium"
                      : "text-ink-2 hover:bg-surface-hover hover:text-ink-1",
                  )}
                >
                  {item.title}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Sidebar;
